/**
 * Service Area utilities.
 *
 * Define a geographic service area for service providers.
 * Buyers can check if an address falls within the provider's service area.
 *
 * Service area stored in listing/user publicData:
 * {
 *   type: 'radius' | 'polygon' | 'regions',
 *   centerLat: number,    — for radius type
 *   centerLng: number,
 *   radiusKm: number,
 *   regions: string[],    — for regions type: country/state/city codes
 *   polygon: {lat, lng}[] — for polygon type
 * }
 */

const EARTH_RADIUS_KM = 6371;

/**
 * Calculate distance between two points (Haversine).
 * @param {number} lat1 @param {number} lng1 @param {number} lat2 @param {number} lng2
 * @returns {number} km
 */
const haversine = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => d * (Math.PI / 180);
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Check if a point is within a radius.
 * @param {number} lat @param {number} lng — the point to check
 * @param {number} centerLat @param {number} centerLng @param {number} radiusKm
 * @returns {boolean}
 */
export const isWithinRadius = (lat, lng, centerLat, centerLng, radiusKm) =>
  haversine(centerLat, centerLng, lat, lng) <= radiusKm;

/**
 * Check if a region code is in the service area.
 * @param {string} regionCode — e.g. 'FI', 'FI-18' (Helsinki region)
 * @param {string[]} serviceRegions
 * @returns {boolean}
 */
export const isInRegion = (regionCode, serviceRegions) => {
  if (!Array.isArray(serviceRegions)) return false;
  const code = regionCode?.toUpperCase();
  return serviceRegions.some(r => code?.startsWith(r.toUpperCase()));
};

/**
 * Check if address/location is within the service area.
 * @param {{ lat, lng, regionCode }} address
 * @param {Object} serviceArea
 * @returns {boolean}
 */
export const isInServiceArea = (address, serviceArea) => {
  if (!serviceArea || !address) return false;
  switch (serviceArea.type) {
    case 'radius':
      if (!address.lat || !address.lng) return false;
      return isWithinRadius(address.lat, address.lng, serviceArea.centerLat, serviceArea.centerLng, serviceArea.radiusKm);
    case 'regions':
      return isInRegion(address.regionCode, serviceArea.regions);
    default:
      return false;
  }
};

/**
 * Get service area from listing.
 * @param {Object|null} listing
 * @returns {Object|null}
 */
export const getServiceArea = (listing) =>
  listing?.attributes?.publicData?.serviceArea || null;
